'use client';

import { useEffect, useRef } from 'react';
import { trackEvent } from '../../utils/analytics';

interface Props {
  htmlContent?: string;
  children?: React.ReactNode;
  className?: string;
}

const trackableDomains = [
  'kavak.com',
  'www.kavak.com',
  'carvak.com',
  'www.carvak.com',
  'kavak.io',
  'www.kavak-frontend.dev.kavak.io'
];

// Define the exact paths for 'sale_selected' (without trailing slashes)
const salePaths: ReadonlySet<string> = new Set([
  '/mx/seminuevos',
  '/br/seminovos',
  '/ar/usados',
  '/cl/usados',
  '/tr/satilik-arac',
]);

// Define the exact paths for 'purchase_selected' (without trailing slashes)
const purchasePaths: ReadonlySet<string> = new Set([
  '/mx/vender-mi-auto',
  '/br/vender-carro',
  '/ar/vender',
  '/cl/vende-tu-auto',
  '/tr/araba-sat', // Note: from carvak.com
]);

// Define the exact paths for 'purchaseFunnel_started' (without trailing slashes)
const purchaseFunnelPaths: ReadonlySet<string> = new Set([
  '/mx/venta-antiguedad-auto',
  '/mx/cotizar-auto/venta-antiguedad-auto',
  '/br/cotacao-carro/venda-anos-carro',
  '/br/venda-anos-carro',
  '/ar/cotizar-auto/venta-antiguedad-auto',
  '/ar/venta-antiguedad-auto',
  '/cl/cotizar-auto/venta-antiguedad-auto',
  '/tr/arac-degerle/arabani-sat-yil', // Note: from carvak.com
]);

// Define the exact paths for 'financing_selected' (without trailing slashes)
const financingPaths: ReadonlySet<string> = new Set([
  '/mx/financiamiento-de-autos',
  '/ar/creditos-para-autos',
  '/tr/tasit-kredisi', // Note: from carvak.com
]);

// Define the exact paths for 'priceguide_selected' (without trailing slashes)
const priceGuidePaths: ReadonlySet<string> = new Set([
  '/mx/guia-de-precios-de-autos',
  '/br/guia-de-precos-de-carros',
  '/ar/precios-de-autos-usados',
]);

// Define the exact paths for 'home_selected' (without trailing slashes)
const homePaths: ReadonlySet<string> = new Set([
  '/mx',
  '/br',
  '/ar',
  '/cl',
  '/tr', // Note: from carvak.com
]);

// Define path prefixes for 'vip_selected' (without trailing slashes)
const vipPathPrefixes: ReadonlyArray<string> = [
  '/mx/usado',
  '/br/venda',
  '/ar/venta',
  '/cl/venta',
  '/tr/ikinci-el',
];

// Define path prefixes for 'catalogFilter_selected' (without trailing slashes)
const catalogPathPrefixes: ReadonlyArray<string> = [
  '/mx/seminuevos',
  '/br/seminovos',
  '/ar/usados',
  '/cl/usados',
  '/tr/satilik-arac',
];


type PathMatcher =
  | { type: 'exact'; paths: ReadonlySet<string>; eventName: string }
  | { type: 'prefix'; prefixes: ReadonlyArray<string>; eventName: string; useOriginalPath?: boolean }
  | { type: 'catalog'; prefixes: ReadonlyArray<string>; eventName: string }; // Special case for catalog with param reconstruction

// Define matchers in priority order
const pathMatchers: ReadonlyArray<PathMatcher> = [
  // 1. VIP (prefix, uses original path)
  { type: 'prefix', prefixes: vipPathPrefixes, eventName: 'vip_selected', useOriginalPath: true },
  // 2. Purchase Funnel (exact)
  { type: 'exact', paths: purchaseFunnelPaths, eventName: 'purchaseFunnel_started' },
  // 3. Purchase (exact)
  { type: 'exact', paths: purchasePaths, eventName: 'purchase_selected' },
  // 4. Financing (exact)
  { type: 'exact', paths: financingPaths, eventName: 'financing_selected' },
  // 5. Price Guide (exact)
  { type: 'exact', paths: priceGuidePaths, eventName: 'priceguide_selected' },
  // 6. Sale (exact)
  { type: 'exact', paths: salePaths, eventName: 'sale_selected' },
  // 7. Home (exact)
  { type: 'exact', paths: homePaths, eventName: 'home_selected' },
  // 8. Catalog Filter (prefix + special logic)
  { type: 'catalog', prefixes: catalogPathPrefixes, eventName: 'catalogFilter_selected' },
];


/**
 * Determines the analytics event name and the path to track based on URL path and query params.
 * Includes logic to reconstruct path for catalog filters based on maker/model/year params.
 * @param originalPath - The original pathname from the URL (e.g., /mx/seminuevos)
 * @param searchParams - The URLSearchParams object from the URL
 * @returns An object with eventName (string | null) and trackedPath (string)
 */
function getTrackedPathAndEvent(originalPath: string, searchParams: URLSearchParams): {
  eventName: string | null;
  trackedPath: string;
} {
  // Normalize path: remove trailing slash if present
  const normalizedPath = originalPath.replace(/\/$/, '');
  let eventName: string | null = null;
  let trackedPath: string = normalizedPath; // Default to normalized path

  for (const matcher of pathMatchers) {
    let matchFound = false;
    let currentPrefix: string | undefined = undefined; // Keep track of prefix for catalog/vip

    switch (matcher.type) {
      case 'exact':
        if (matcher.paths.has(normalizedPath)) {
          matchFound = true;
        }
        break;
      case 'prefix':
        currentPrefix = matcher.prefixes.find(prefix => normalizedPath.startsWith(prefix));
        if (currentPrefix) {
          matchFound = true;
          // VIP specifically needs the original path
          if (matcher.useOriginalPath) {
            trackedPath = originalPath;
          }
        }
        break;
      case 'catalog':
        currentPrefix = matcher.prefixes.find(prefix => normalizedPath.startsWith(prefix));
        if (currentPrefix) {
          matchFound = true;
          // Catalog reconstruction logic
          const maker = searchParams.get('maker');
          const model = searchParams.get('model');
          const year = searchParams.get('year');
          if (maker || model || year) {
            let reconstructed = currentPrefix;
            if (maker) reconstructed += `/${maker}`;
            if (model) reconstructed += `/${model}`;
            if (year) reconstructed += `/${year}`;
            trackedPath = reconstructed;
          }
          // If no params, trackedPath remains normalizedPath (handled by default init)
        }
        break;
    }

    if (matchFound) {
      eventName = matcher.eventName;
      break; // Found the highest priority match, exit loop
    }
  }

  return { eventName, trackedPath };
}

export function TrackedHtmlContent({ htmlContent, children, className }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      // Find the closest ancestor anchor tag, starting from the event target
      // This handles cases where the click might be on an element inside the anchor (e.g., <strong>)
      const linkElement = (event.target as Element).closest('a');

      // Ensure the click originated from an anchor tag within the container
      if (!linkElement || !containerRef.current?.contains(linkElement)) {
        return;
      }

      const href = linkElement.getAttribute('href');

      if (!href) return;

      try {
        const url = new URL(href);

        if (!trackableDomains.includes(url.hostname)) {
          return;
        }

        // Call the helper function to get event details
        const { eventName, trackedPath } = getTrackedPathAndEvent(url.pathname, url.searchParams);

        console.info('Original path:', url.pathname, 'Tracked path:', trackedPath, 'Event:', eventName, 'Params:', {
          maker: url.searchParams.get('maker'),
          model: url.searchParams.get('model'),
          year: url.searchParams.get('year')
        });

        // If we identified an event, track it and open in new tab
        if (eventName) {
          // Prevent default navigation (stay on the blog page)
          event.preventDefault();

          // Open the link in a new tab
          window.open(href, '_blank', 'noopener,noreferrer');

          // Track the event using the determined path and event name
          trackEvent({
            event: eventName,
            flow: 'blog_post',
            path: trackedPath, // Use the path from the helper function
          });
        }
      } catch (error) {
        // Log errors for invalid URLs but don't crash
        console.warn('[TrackedHtmlContent] Could not parse URL for tracking:', href, error);
      }
    };

    const container = containerRef.current;
    if (container) {
      // Attach the listener to the container
      container.addEventListener('click', handleClick);
    }

    // Cleanup: remove the listener when the component unmounts
    return () => {
      if (container) {
        container.removeEventListener('click', handleClick);
      }
    };
  }, []); // Run only once on mount

  // Render the container, the optional HTML content, and any children
  return (
    <div
      ref={containerRef}
      className={className}
    >
      {htmlContent && (
        <div
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
      )}
      {children}
    </div>
  );
}